import React, { useEffect, useState } from 'react';
import DocPageHeader from 'components/docs/DocPageHeader';
import { useAuth } from '../hooks/useAuth';
import useCustomer from '../hooks/useCustomer';
import { Form, Row, Col, Button, Card } from 'react-bootstrap';

const Profile: React.FC = () => {
  const auth = useAuth();
  const { profile, updateProfile, loading } = useCustomer();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Update form data when profile is loaded
  useEffect(() => {
    if (profile) {
      const billingData: Record<string, string> = {};
      Object.keys(profile)
        .filter(key => key.startsWith('billing_'))
        .forEach(key => {
          billingData[key] = profile[key] || '';
        });
      setFormData(billingData);
    }
  }, [profile]);

  // Helper function to format field names for display
  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace('billing_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Get all billing fields from profile
  const getBillingFields = () => {
    if (!profile) return [];
    
    return Object.keys(profile)
      .filter(key => key.startsWith('billing_'))
      .map(key => ({
        key,
        label: formatFieldName(key),
        value: formData[key] || ''
      }));
  };

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    // Clear save message when user starts typing
    if (saveMessage) {
      setSaveMessage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profile) {
      setSaveMessage({ type: 'error', text: 'Profile data not loaded' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      // Backend only allows updating billing_* fields; send ONLY those.
      const billingOnlyUpdate = { ...formData };
      console.log('Sending billing-only profile update:', billingOnlyUpdate);

      await updateProfile(billingOnlyUpdate);
      setSaveMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      // Refresh profile data after successful update
      // The profile will be automatically updated by useCustomer hook
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to update profile' 
      });
    } finally {
      setSaving(false);
    }
  };

  const billingFields = getBillingFields();

  return (
    <div>
      <DocPageHeader title="Profile" />
      <Card>
        <Card.Body>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {saveMessage && (
                <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} mb-3`}>
                  {saveMessage.text}
                </div>
              )}
              {billingFields.map((field) => (
                <Form.Group as={Row} className="mb-3" controlId={field.key} key={field.key}>
                  <Form.Label column sm={2}>
                    {field.label}
                  </Form.Label>
                  <Col sm={10}>
                    <Form.Control 
                      type={field.key.includes('email') ? 'email' : 'text'}
                      placeholder={field.label}
                      value={field.value}
                      onChange={(e) => handleInputChange(field.key, e.target.value)}
                      disabled={saving}
                    />
                  </Col>
                </Form.Group>
              ))}

              <Form.Group as={Row} className="mb-3">
                <Col sm={{ span: 10, offset: 2 }}>
                  <Button type="submit" variant="primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Col>
              </Form.Group>
            </Form>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Profile;

